import { find, isNumber } from "lodash";
import { useMemo } from "react";
import {
  amortizationMethodsListCompanyLoan,
  FormatNumber,
  isPaymentFrequencyConfigurable,
  paymentFrequencyList,
} from "utils";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import { EarlyRepaymentCommentBox } from "@krea/shared/features/early-repayment";
import type { Currency } from "@krea/shared/types/common";
import { formatLenderMonthlyInterestRate } from "@krea/shared/utils/formatNumber";

import { AmortizationMethodEnum } from "@/features/common/enums";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import type { ICompanyLoanBid } from "@/features/lender-responses/types/lender-response";

import type { IBidDetailsResponse } from "../../BidDetails";
import {
  BidDetailsListItem,
  BidFeesIncludedNote,
  EmptyField,
} from "../../bidHelpers";
import { earlyRepaymentLabel } from "../shared/earlyRepaymentLabel";

interface ILoanBidProps {
  currency: Currency;
  bid: ICompanyLoanBid;
}

export const LoanBid = ({
  bid,
  currency,
}: ILoanBidProps): IBidDetailsResponse => {
  const [formattedCurrencyValue] = useFormatCurrency({
    currency: currency,
  });

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const amortizationMethodsText = find(
    amortizationMethodsListCompanyLoan,
    (method) => method.value === bid.amortizationMethod,
  );

  // Only bullet-like methods can defer, so on every other method the row would
  // state the one value it is allowed to have.
  const paymentFrequencyText = isPaymentFrequencyConfigurable(
    bid.amortizationMethod,
  )
    ? find(
        paymentFrequencyList,
        (frequency) => frequency.value === bid.paymentFrequency,
      )
    : undefined;

  const averageMonthlyPayment = bid.averageMonthlyPayment;
  const comparableMonthlyInterestRate = bid.comparableMonthlyInterestRate;

  const summary = useMemo(() => {
    return (
      <div className="flex w-full flex-col gap-2 xl:grid xl:grid-cols-4 xl:gap-4">
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Amount"
            value={
              bid.amount ? formattedCurrencyValue(bid.amount) : <EmptyField />
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Repayment time"
            value={
              bid.paybackTimeMonths ? (
                `${bid.paybackTimeMonths} ${bid.paybackTimeMonths > 1 ? "months" : "month"}`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Monthly payment"
            value={
              averageMonthlyPayment ? (
                formattedCurrencyValue(averageMonthlyPayment)
              ) : (
                <EmptyField />
              )
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <BidDetailsListItem
            variant="summary"
            title="Effective monthly interest"
            value={
              comparableMonthlyInterestRate ? (
                `${FormatNumber(comparableMonthlyInterestRate)}%`
              ) : (
                <EmptyField />
              )
            }
          />
        </div>
      </div>
    );
  }, [
    bid,
    formattedCurrencyValue,
    averageMonthlyPayment,
    comparableMonthlyInterestRate,
  ]);

  const details = useMemo(() => {
    const earlyRepayment = earlyRepaymentLabel(
      bid.earlyRepayment,
      formattedCurrencyValue,
    );
    return (
      <>
        <div>
          {!!amortizationMethodsText && !!amortizationMethodsText.label && (
            <BidDetailsListItem
              title="Amortisation type"
              value={amortizationMethodsText.label}
            />
          )}

          {!!paymentFrequencyText && (
            <BidDetailsListItem
              title="Payment frequency"
              value={paymentFrequencyText.label}
            />
          )}

          {bid.amortizationMethod !== AmortizationMethodEnum.FIXED &&
            isNumber(bid.lenderMonthlyInterest) && (
              <BidDetailsListItem
                title={
                  isInterestRatePerYearActivated
                    ? "Yearly interest rate (shown in contract)"
                    : "Monthly interest rate (shown in contract)"
                }
                value={`${formatLenderMonthlyInterestRate(bid.lenderMonthlyInterest, "TO_YEARLY", isInterestRatePerYearActivated)}% ${isInterestRatePerYearActivated ? "year" : "month"}`}
              />
            )}

          {bid.amortizationMethod === AmortizationMethodEnum.FIXED &&
            !!bid.lenderMonthlyAmount && (
              <BidDetailsListItem
                title="Fixed monthly fee shown in contract"
                value={`${formattedCurrencyValue(bid.lenderMonthlyAmount)}/ month`}
              />
            )}

          {(!!bid.averageMonthlyCost || !!bid.averageMonthlyAmortisation) && (
            <>
              {!!bid.averageMonthlyCost && (
                <BidDetailsListItem
                  title="Interest payment"
                  value={formattedCurrencyValue(bid.averageMonthlyCost)}
                />
              )}
              {!!bid.averageMonthlyAmortisation && (
                <BidDetailsListItem
                  title="Principal payment"
                  value={formattedCurrencyValue(bid.averageMonthlyAmortisation)}
                />
              )}
            </>
          )}

          {!!bid.setupFee && (
            <BidDetailsListItem
              title="Setup fee"
              value={formattedCurrencyValue(bid.setupFee)}
            />
          )}

          {!!bid.administrationFees && (
            <BidDetailsListItem
              title="Administration fees (per month)"
              value={formattedCurrencyValue(bid.administrationFees)}
            />
          )}

          <BidDetailsListItem
            title="Cost for early repayment"
            value={earlyRepayment.label}
          />
          {earlyRepayment.comment && (
            <EarlyRepaymentCommentBox
              label="Comment"
              comment={earlyRepayment.comment}
              className="mb-4"
            />
          )}
        </div>
        <BidFeesIncludedNote />
      </>
    );
  }, [
    bid,
    formattedCurrencyValue,
    amortizationMethodsText,
    paymentFrequencyText,
    isInterestRatePerYearActivated,
  ]);

  return {
    summary,
    details,
  };
};
